import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Chip,
  InputAdornment,
  Tooltip,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  PRICING_NOTES_CURRENT_PERIOD,
  PRICING_NOTES_ACTION_STRATEGIES,
  PRICING_NOTES_DROPS_CUTS,
  PRICING_NOTES_PRIMARY_QUANTITY_STATUS,
} from '../../../helpers/constants';
import { useQueryDataIQZones } from '../../../hooks/useEventDataIQZones';

const useStyles = makeStyles(theme => ({
  formContainer: {
    padding: theme.spacing(2),
    maxHeight: '55vh',
    overflowY: 'auto',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: 'primary',
  },
  fieldLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: theme.spacing(0.5),
  },
  requiredField: {
    fontSize: '14px',
    color: 'primary',
  },
  optionalField: {
    fontSize: '14px',
    color: '#7D7D7D',
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(1.5),
  },
  selectSmall: {
    fontSize: '14px',
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  buttonSmall: {
    fontSize: '12px',
    textTransform: 'none',
  },
  zoneButton: {
    fontSize: '11px',
    padding: '4px 12px',
    textTransform: 'none',
    minWidth: 'auto',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  addActionLink: {
    fontSize: '12px',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  actionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  helperText: {
    fontSize: '10px',
    color: '#999',
    marginTop: theme.spacing(0.5),
  },
  checkboxLabel: {
    fontSize: '14px',
  },
  toggleButtonGroup: {
    display: 'flex',
    width: '100%',
    '& > button': {
      borderRadius: 0,
      borderRight: 'none',
      '&:first-child': {
        flex: 2,
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
      },
      '&:last-child': {
        flex: 1.5,
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        borderRight: '1px solid rgba(0, 0, 0, 0.23)',
      },
    },
  },
  zoneRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  zoneChipContainer: {
    width: '100px',
    display: 'flex',
  },
  zoneChip: {
    fontSize: '11px',
    height: '21px',
    minWidth: '40px',
    maxWidth: '100px',
    '& .MuiChip-deleteIcon': {
      fontSize: '14px !important',
      width: '14px',
      height: '14px',
    },
  },
  actionChip: {
    fontSize: '11px',
    height: '21px',
    minWidth: '100px',
    flex: 1,
    margin: theme.spacing(0.5),
    '& .MuiChip-deleteIcon': {
      fontSize: '14px !important',
      width: '14px',
      height: '14px',
    },
  },
  actionChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
  },
  requiredAsterisk: {
    color: '#C7615A',
  },
}));

// Validation schema using Zod
const validationSchema = z
  .object({
    currentPeriod: z.string().nonempty('Current Period is required'),
    endpoint: z.string().optional(),
    soldPercentage: z.string().nonempty('% Sold is required'),
    primaryQuantityStatus: z.string().nonempty('Primary Quantity Status is required'),
    pmDropsCuts: z.string().optional(),
    getInPrice: z.string().optional(),
    bidPriceDelta: z.string().optional(),
    kobeSelloutDTE: z.string().optional(),
    dataIQSales: z.string().optional(),
    selectedZones: z.array(z.string()),
    zoneData: z.record(
      z.object({
        avgSalesPrice: z.string().optional(),
        totalSales: z.string().optional(),
      })
    ),
    action: z.string().nonempty('Action is required'),
    pricingStrategy: z.string().nonempty('Pricing Strategy is required'),
    withCeiling: z.boolean(),
    ceilingReason: z.string(),
  })
  .refine(
    data => {
      // If primaryQuantityStatus is NOT Sold Out, pmDropsCuts is required
      if (data.primaryQuantityStatus !== PRICING_NOTES_PRIMARY_QUANTITY_STATUS.SOLD_OUT) {
        return data.pmDropsCuts && data.pmDropsCuts.trim().length > 0;
      }
      return true;
    },
    {
      message: 'PM Drops / Cuts is required',
      path: ['pmDropsCuts'],
    }
  )
  .refine(
    data => {
      // If primaryQuantityStatus is NOT Sold Out, getInPrice is required
      if (data.primaryQuantityStatus !== PRICING_NOTES_PRIMARY_QUANTITY_STATUS.SOLD_OUT) {
        return data.getInPrice && data.getInPrice.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Get In Price is required',
      path: ['getInPrice'],
    }
  )
  .refine(
    data => {
      // If currentPeriod is "Onsale", endpoint is required
      if (data.currentPeriod === 'Onsale' || data.currentPeriod === 'Post Onsale') {
        return data.endpoint && data.endpoint.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Endpoint is required when period is Onsale or Post Onsale',
      path: ['endpoint'],
    }
  )
  .refine(
    data => {
      // If withCeiling is true, ceilingReason must be filled
      if (data.withCeiling && !data.ceilingReason.trim()) {
        return false;
      }
      return true;
    },
    {
      message: 'Ceiling reason is required when ceiling is selected',
      path: ['ceilingReason'],
    }
  )
  .refine(
    data => {
      // If no zones are selected, validation passes
      if (data.selectedZones.length === 0) return true;

      // For each selected zone, avgSalesPrice and totalSales must be filled
      return data.selectedZones.every(zone => {
        const zoneInfo = data.zoneData[zone];
        // Check if zoneInfo exists and has both fields filled
        if (!zoneInfo) return false;
        if (!zoneInfo.avgSalesPrice || typeof zoneInfo.avgSalesPrice !== 'string') return false;
        if (!zoneInfo.totalSales || typeof zoneInfo.totalSales !== 'string') return false;
        // Check if the strings are not empty after trimming
        return zoneInfo.avgSalesPrice.trim().length > 0 && zoneInfo.totalSales.trim().length > 0;
      });
    },
    {
      message: 'Avg. Sales Price and Total Sales are required for selected zones',
      path: ['zoneData'],
    }
  );

const DEFAULT_FORM_VALUES = {
  currentPeriod: '',
  endpoint: '',
  soldPercentage: '',
  primaryQuantityStatus: '',
  pmDropsCuts: '',
  getInPrice: '',
  bidPriceDelta: '',
  kobeSelloutDTE: '',
  dataIQSales: '',
  selectedZones: [],
  zoneData: {},
  action: '',
  pricingStrategy: '',
  withCeiling: false,
  ceilingReason: '',
};

const PricingNoteForm = ({ onSubmit, setSubmitEnabled, onFormReady, eventId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDarkMode = theme.palette.type === 'dark';

  // Fetch zones from API
  const { data: zonesData } = useQueryDataIQZones(eventId);
  const zones = zonesData?.response || [];
  const hasZones = zones.length > 0;

  // Map zones to the format needed for the UI
  const availableZones = zones.map(zone => ({
    id: zone,
    label: zone,
  }));

  const [showAddAction, setShowAddAction] = useState(false);

  // Helper function to get chip colors based on theme and selection state
  const getChipColors = isSelected => {
    if (isSelected) {
      return {
        backgroundColor: isDarkMode ? '#EBD3EF' : '#F5E9F7',
        color: isDarkMode ? '#9C27B0' : '#9C27B0',
      };
    }
    return {
      backgroundColor: isDarkMode ? '#00000040' : '#9FA4AB4D',
      color: isDarkMode ? '#FFFFFF' : '#6B7280',
    };
  };

  // Helper function to render label with red asterisk for required fields
  const renderLabel = (text, required = false) => {
    if (!required) return text;
    return (
      <>
        {text}
        <span className={classes.requiredAsterisk}>*</span>
      </>
    );
  };

  // Initialize react-hook-form with validation
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    resolver: zodResolver(validationSchema),
    mode: 'all', // Validate on every change
    reValidateMode: 'onChange',
  });

  // Watch form values
  const currentPeriod = watch('currentPeriod');
  const primaryQuantityStatus = watch('primaryQuantityStatus');
  const selectedZones = watch('selectedZones') || [];
  const withCeiling = watch('withCeiling');
  const allFormValues = watch();

  // Determine if PM Drops / Cuts and Get In Price should be disabled
  const isSoldOut = primaryQuantityStatus === PRICING_NOTES_PRIMARY_QUANTITY_STATUS.SOLD_OUT;

  // Handler for form submission - receives validated form data
  const handleFormSubmit = formData => {
    // Add note type to the form data
    const submissionData = {
      ...formData,
      standardizeNote: true,
    };
    // Call the parent's onSubmit with the validated form data
    if (onSubmit) {
      onSubmit(submissionData);
    }
  };

  // Update parent component when form validity changes
  useEffect(() => {
    setSubmitEnabled(isValid);
  }, [isValid, setSubmitEnabled, errors, allFormValues]);

  // Pass handleSubmit function to parent component when form is ready
  useEffect(() => {
    if (onFormReady) {
      const submitFn = () => handleSubmit(handleFormSubmit)();
      onFormReady(submitFn);
    }
  }, []);

  // Clear endpoint when currentPeriod changes from "Onsale" or "Post Onsale" to something else
  useEffect(() => {
    if (currentPeriod !== 'Onsale' && currentPeriod !== 'Post Onsale') {
      setValue('endpoint', '', { shouldValidate: true });
    }
  }, [currentPeriod, setValue]);

  // Clear PM Drops / Cuts and Get In Price when primaryQuantityStatus is "Sold Out"
  useEffect(() => {
    if (isSoldOut) {
      setValue('pmDropsCuts', '', { shouldValidate: true });
      setValue('getInPrice', '', { shouldValidate: true });
    }
  }, [isSoldOut, setValue]);

  const handleZoneToggle = zone => {
    const isSelected = selectedZones.includes(zone);
    const newZones = isSelected ? selectedZones.filter(sz => sz !== zone) : [...selectedZones, zone];
    setValue('selectedZones', newZones, { shouldValidate: true });

    // If unselecting, clear the zone data
    if (isSelected) {
      const currentZoneData = watch('zoneData') || {};
      const newZoneData = { ...currentZoneData };
      delete newZoneData[zone];
      setValue('zoneData', newZoneData, { shouldValidate: true });
    }
  };

  const isZoneSelected = zone => selectedZones.includes(zone);

  // Helper function to allow only numbers and dots
  const handleNumericInput = (event, field) => {
    const { value } = event.target;
    // Allow only numbers and a single dot
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (regex.test(value)) {
      field.onChange(value);
    }
  };

  return (
    <Box className={classes.formContainer}>
      <Typography className={classes.requiredField}>
        All fields are required (*). Optional fields need details when selected.
      </Typography>

      {/* Pricing Context */}
      <Typography className={classes.sectionTitle} style={{ marginTop: '10px' }}>
        Pricing Context
      </Typography>
      <Grid container spacing={2} style={{ padding: '5px' }}>
        <Grid item xs={6}>
          <Controller
            name="currentPeriod"
            control={control}
            render={({ field }) => (
              <FormControl className={classes.formControl} size="small" error={!!errors.currentPeriod}>
                <InputLabel className={classes.selectSmall}>{renderLabel('Current Period', true)}</InputLabel>
                <Select {...field} className={classes.selectSmall}>
                  {PRICING_NOTES_CURRENT_PERIOD.map(period => (
                    <MenuItem key={period} value={period}>
                      {period}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} style={{ marginTop: -20 }}>
          <Grid container spacing={2}>
            {(currentPeriod === 'Onsale' || currentPeriod === 'Post Onsale') && (
              <Grid item xs={6}>
                <Controller
                  name="endpoint"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={renderLabel('Endpoint', true)}
                      size="small"
                      fullWidth
                      className={classes.formControl}
                      error={!!errors.endpoint}
                      helperText={errors.endpoint?.message}
                      onChange={e => handleNumericInput(e, field)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <Controller
                name="soldPercentage"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={renderLabel('% Sold', true)}
                    size="small"
                    fullWidth
                    className={classes.formControl}
                    error={!!errors.soldPercentage}
                    helperText={errors.soldPercentage?.message}
                    onChange={e => handleNumericInput(e, field)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Primary Market Data */}
      <Typography className={classes.sectionTitle}>Primary Market Data</Typography>
      <Grid container spacing={2} style={{ padding: '5px' }}>
        <Grid item xs={12}>
          <Grid container style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" className={classes.fieldLabel}>
                Primary Quantity Status*
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Controller
                name="primaryQuantityStatus"
                control={control}
                render={({ field }) => (
                  <Box className={classes.toggleButtonGroup}>
                    <Button
                      variant={
                        field.value === PRICING_NOTES_PRIMARY_QUANTITY_STATUS.TICKETS_AVAILABLE
                          ? 'contained'
                          : 'outlined'
                      }
                      color={
                        field.value === PRICING_NOTES_PRIMARY_QUANTITY_STATUS.TICKETS_AVAILABLE ? 'primary' : 'default'
                      }
                      size="small"
                      className={classes.buttonSmall}
                      onClick={() => field.onChange(PRICING_NOTES_PRIMARY_QUANTITY_STATUS.TICKETS_AVAILABLE)}
                    >
                      {PRICING_NOTES_PRIMARY_QUANTITY_STATUS.TICKETS_AVAILABLE}
                    </Button>
                    <Button
                      variant={
                        field.value === PRICING_NOTES_PRIMARY_QUANTITY_STATUS.SOLD_OUT ? 'contained' : 'outlined'
                      }
                      color={field.value === PRICING_NOTES_PRIMARY_QUANTITY_STATUS.SOLD_OUT ? 'primary' : 'default'}
                      size="small"
                      className={classes.buttonSmall}
                      onClick={() => field.onChange(PRICING_NOTES_PRIMARY_QUANTITY_STATUS.SOLD_OUT)}
                    >
                      {PRICING_NOTES_PRIMARY_QUANTITY_STATUS.SOLD_OUT}
                    </Button>
                  </Box>
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name="pmDropsCuts"
                control={control}
                render={({ field }) => (
                  <FormControl
                    className={classes.formControl}
                    size="small"
                    error={!!errors.pmDropsCuts}
                    disabled={isSoldOut}
                  >
                    <InputLabel className={classes.selectSmall}>
                      {renderLabel('PM Drops / Cuts', !isSoldOut)}
                    </InputLabel>
                    <Select {...field} className={classes.selectSmall} disabled={isSoldOut}>
                      {PRICING_NOTES_DROPS_CUTS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="getInPrice"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={renderLabel('Get In Price', !isSoldOut)}
                    size="small"
                    fullWidth
                    disabled={isSoldOut}
                    className={classes.formControl}
                    error={!!errors.getInPrice}
                    helperText={errors.getInPrice?.message}
                    InputProps={{
                      startAdornment: field.value ? <InputAdornment position="start">$</InputAdornment> : null,
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Data to Running Sales Price Per Zone - Only show when zones exist */}
      {hasZones && (
        <>
          <Grid container alignItems="center" spacing={1}>
            <Grid item>
              <Typography className={classes.sectionTitle}>Data IQ Sales and Avg. Sales Price Per Zone</Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.optionalField}>(Optional)</Typography>
            </Grid>
          </Grid>
          <Grid container spacing={2} style={{ padding: '5px' }}>
            <Grid item xs={6}>
              <Controller
                name="dataIQSales"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Data IQ Running Sales"
                    size="small"
                    fullWidth
                    className={classes.formControl}
                    error={!!errors.dataIQSales}
                    helperText={errors.dataIQSales?.message}
                    onChange={e => handleNumericInput(e, field)}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography>Select zones</Typography>

          {availableZones.map(zone => {
            const selected = isZoneSelected(zone.id);
            return (
              <Box key={zone.id} className={classes.zoneRow}>
                <Box className={classes.zoneChipContainer}>
                  <Tooltip title={zone.label} arrow placement="top">
                    <Chip
                      label={zone.label}
                      onClick={() => handleZoneToggle(zone.id)}
                      onDelete={() => handleZoneToggle(zone.id)}
                      deleteIcon={
                        selected ? (
                          <CancelIcon style={{ fontSize: 10, color: getChipColors(selected).color }} />
                        ) : (
                          <AddCircleOutlineIcon style={{ fontSize: 10, color: getChipColors(selected).color }} />
                        )
                      }
                      className={classes.zoneChip}
                      style={{
                        ...getChipColors(selected),
                        fontWeight: selected ? 800 : 600,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                </Box>
                <Controller
                  name={`zoneData.${zone.id}.avgSalesPrice`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Avg. Sales Price"
                      size="small"
                      style={{ flex: 1 }}
                      disabled={!selected}
                      value={field.value || ''}
                      InputProps={{
                        startAdornment: field.value ? <InputAdornment position="start">$</InputAdornment> : null,
                      }}
                    />
                  )}
                />
                <Controller
                  name={`zoneData.${zone.id}.totalSales`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Total Sales"
                      size="small"
                      style={{ flex: 1 }}
                      disabled={!selected}
                      value={field.value || ''}
                    />
                  )}
                />
              </Box>
            );
          })}
        </>
      )}

      {/* Bid Price Delta and Kobe Sellout DTE */}
      <Grid container alignItems="center" spacing={1}>
        <Grid item>
          <Typography className={classes.sectionTitle}>Pricing Automations</Typography>
        </Grid>
        <Grid item>
          <Typography className={classes.optionalField}>(Optional)</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ padding: '5px' }}>
        <Grid item xs={6}>
          <Controller
            name="kobeSelloutDTE"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Kobe Target Sellout DTE"
                size="small"
                fullWidth
                className={classes.formControl}
                error={!!errors.kobeSelloutDTE}
                helperText={errors.kobeSelloutDTE?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="bidPriceDelta"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Bid Price Delta"
                size="small"
                fullWidth
                className={classes.formControl}
                error={!!errors.bidPriceDelta}
                helperText={errors.bidPriceDelta?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Actions & Strategies */}
      <Typography className={classes.sectionTitle}>Actions & Strategies</Typography>
      <Grid container spacing={2} style={{ padding: '5px' }}>
        <Grid item xs={6}>
          <Typography>
            Action Taken
            <span className={classes.requiredAsterisk}>*</span>
          </Typography>
        </Grid>
        <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography className={classes.addActionLink} onClick={() => setShowAddAction(!showAddAction)}>
            + Add Action
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="action"
            control={control}
            render={({ field }) => (
              <Box className={classes.actionChipsContainer}>
                {PRICING_NOTES_ACTION_STRATEGIES.map(strategy => {
                  const isSelected = field.value === strategy;
                  return (
                    <Chip
                      key={strategy}
                      label={strategy}
                      onClick={() => {
                        field.onChange(isSelected ? '' : strategy);
                        if (showAddAction) setShowAddAction(false);
                      }}
                      onDelete={() => {
                        field.onChange(isSelected ? '' : strategy);
                        if (showAddAction) setShowAddAction(false);
                      }}
                      deleteIcon={
                        isSelected ? (
                          <CancelIcon style={{ fontSize: 10, color: getChipColors(isSelected).color }} />
                        ) : (
                          <AddCircleOutlineIcon style={{ fontSize: 10, color: getChipColors(isSelected).color }} />
                        )
                      }
                      className={classes.actionChip}
                      style={{
                        ...getChipColors(isSelected),
                        fontWeight: isSelected ? 800 : 600,
                        cursor: 'pointer',
                      }}
                    />
                  );
                })}
              </Box>
            )}
          />
        </Grid>

        {showAddAction && (
          <Grid item xs={6}>
            <Controller
              name="action"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Add Action"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && field.value?.trim()) {
                      e.preventDefault();
                      setShowAddAction(false);
                    }
                  }}
                  size="small"
                  fullWidth
                  autoFocus
                />
              )}
            />
          </Grid>
        )}
      </Grid>

      <Controller
        name="pricingStrategy"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={renderLabel('Pricing Strategy & Execution', true)}
            multiline
            minRows={3}
            maxRows={6}
            variant="outlined"
            size="small"
            fullWidth
            className={classes.formControl}
            style={{ marginTop: 16 }}
            error={!!errors.pricingStrategy}
            helperText={errors.pricingStrategy?.message}
          />
        )}
      />

      <Controller
        name="withCeiling"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)} color="primary" />
            }
            label={<span className={classes.checkboxLabel}>With Ceiling? (Optional)</span>}
          />
        )}
      />

      {withCeiling && (
        <Controller
          name="ceilingReason"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={renderLabel('Ceiling Reason', true)}
              multiline
              minRows={2}
              maxRows={4}
              variant="outlined"
              size="small"
              fullWidth
              className={classes.formControl}
              placeholder="Enter reason for ceiling"
              error={!!errors.ceilingReason}
              helperText={errors.ceilingReason?.message}
            />
          )}
        />
      )}
    </Box>
  );
};

PricingNoteForm.propTypes = {
  onSubmit: PropTypes.func,
  setSubmitEnabled: PropTypes.func,
  onFormReady: PropTypes.func,
  eventId: PropTypes.string,
};

PricingNoteForm.defaultProps = {
  onSubmit: () => {},
  setSubmitEnabled: () => {},
  onFormReady: undefined,
  eventId: '',
};

export default PricingNoteForm;
